import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { Label, TextField } from "react-aria-components";
import type { AuthSession } from "../../auth/keycloak.js";
import { Drawer, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { useFacilityCategories } from "./useFacilityCategories.js";
import { useCreateManual, type ManualMutationError } from "./useManualMutations.js";
import { PlatformSelector, type ManualPlatforms } from "./PlatformSelector.js";

const NAME_MAX = 255;
const DESC_MAX = 20000;

export interface CreateManualDrawerProps {
  facilityId: number;
  session: AuthSession | null;
  onClose: () => void;
  /** The new manual — the caller opens it in the editor (owner 2026-11-04). */
  onCreated?: (manual: { id: number; name: string }) => void;
}

/**
 * Create a manual in the selected facility (ADR-013). A right slide-over (Drawer, not a dialog — the form is tall +
 * hosts the rich-text editor): a **Category** select (the facility's attached categories), a **Name**, a **rich-text
 * description** (TipTap → HTML, sanitized server-side), and an **"Available for AR/VR"** switch. Create posts to
 * `POST /facilities/:id/manuals`; the manual lands in the grid via query invalidation. (AI Assistant + Import are out
 * of scope — ADR-013.)
 */
export function CreateManualDrawer({ facilityId, session, onClose, onCreated }: CreateManualDrawerProps) {
  const { t } = useTranslation();
  const categories = useFacilityCategories(session, facilityId);
  const create = useCreateManual(session);
  // The first category is the choice until another is picked (owner 2026-11-04): one click fewer, and it can still change.
  const [pickedCategoryId, setCategoryId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [platforms, setPlatforms] = useState<ManualPlatforms>({ is2dWebManual: true, is3dWebManual: false, isVrManual: false, isArManual: false });

  const cats = categories.data ?? [];
  const categoryId = pickedCategoryId ?? cats[0]?.id ?? null;
  const nameTrimmed = name.trim();
  const nameTooLong = nameTrimmed.length > NAME_MAX;
  const descTooLong = description.length > DESC_MAX;
  const canCreate =
    categoryId !== null && nameTrimmed.length > 0 && !nameTooLong && !descTooLong && !create.isPending;

  const close = () => {
    create.reset();
    onClose();
  };
  // Send the HTML unless the editor is in its EMPTY state (TipTap emits `<p></p>` for an empty doc). We must NOT reuse
  // the excerpt-only `htmlToText` here — it rewrites `<hr>`/lists to whitespace, so textless-but-structural content
  // (a lone horizontal rule, an empty list) would be dropped, contradicting the server sanitizer's `<hr>` carve-out
  // (diff-panel #1). The server sanitizer is the authority on what's meaningful (it null-empties truly-empty content).
  const trimmedDesc = description.trim();
  const descIsEmpty = trimmedDesc === "" || trimmedDesc === "<p></p>";

  const onSubmit = () => {
    if (!canCreate || categoryId === null) return;
    create.mutate(
      {
        facilityId,
        categoryId,
        name: nameTrimmed,
        ...platforms,
        description: descIsEmpty ? undefined : description,
      },
      {
        onSuccess: (m) => {
          onCreated?.({ id: m.id, name: m.name });
          close();
        },
      },
    );
  };

  return (
    <Drawer isOpen isDismissable={!create.isPending} onOpenChange={(open) => !open && close()} className="gap-4">
      <DrawerHeader>
        <DrawerTitle>{t("manualManagement.create.title")}</DrawerTitle>
        <Button variant="ghost" size="icon" aria-label={t("common.close")} onPress={close} isDisabled={create.isPending}>
          <X className="size-4" />
        </Button>
      </DrawerHeader>

      {create.isError ? (
        <p role="alert" className="text-destructive text-sm">
          {createErrorCopy(create.error, t)}
        </p>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
        {/* Category — the facility's attached categories (a small, known set → single-select CHIPS, not a dropdown;
            mirrors MoveManualDrawer's category step). */}
        <div className="flex flex-col gap-1.5" role="group" aria-label={t("manual.manualSettings.category")}>
          <span className="text-sm font-medium">{t("manual.manualSettings.category")}</span>
          {categories.isPending ? (
            <p className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("facilityManagement.create.loadingCategories")}
            </p>
          ) : categories.isError ? (
            <div className="text-muted-foreground flex flex-col items-start gap-2 text-sm">
              <span>{t("manualManagement.create.loadCategoriesFailed")}</span>
              <Button variant="outline" size="sm" onPress={() => void categories.refetch()} isDisabled={categories.isFetching}>
                {t("common.tryAgain")}
              </Button>
            </div>
          ) : cats.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("manualManagement.create.noCategories")}</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {cats.map((cat) => (
                <Button
                  key={cat.id}
                  // Round pills. SELECTED = primary (`default`) — inverts per theme via the tokens: dark-blue-on-white in
                  // light, a light pill with dark text in dark. UNSELECTED = a visible muted fill (`secondary`), not a
                  // faint outline (which vanished on the dark surface). Always verify both themes.
                  variant={categoryId === cat.id ? "default" : "secondary"}
                  size="sm"
                  className="rounded-full px-3"
                  aria-pressed={categoryId === cat.id}
                  onPress={() => setCategoryId(cat.id)}
                >
                  {cat.name}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Name */}
        <TextField className="flex flex-col gap-1.5" value={name} onChange={setName} isRequired>
          <Label className="text-sm font-medium">{t("common.name")}</Label>
          <Input placeholder={t("manualManagement.create.namePlaceholder")} maxLength={NAME_MAX} autoFocus />
        </TextField>

        {/* Description (rich text) */}
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">{t("manual.manualSettings.description")}</span>
          <RichTextEditor ariaLabel={t("manual.manualSettings.description")} value={description} onChange={setDescription} />
          {descTooLong ? (
            <p className="text-destructive text-xs">{t("manualManagement.create.descTooLong", { length: description.length.toLocaleString(), max: DESC_MAX.toLocaleString() })}</p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">{t("manualManagement.platforms.title")}</span>
          <PlatformSelector value={platforms} onChange={setPlatforms} />
        </div>
      </div>

      <DrawerFooter>
        <Button variant="ghost" onPress={close} isDisabled={create.isPending}>
          {t("other.cancelButton")}
        </Button>
        <Button variant="default" onPress={onSubmit} isDisabled={!canCreate}>
          {create.isPending ? (
            <>
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              {t("manualManagement.create.creating")}
            </>
          ) : (
            t("other.createButton")
          )}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
}

function createErrorCopy(err: unknown, t: TFunction): string {
  const e = err as ManualMutationError | undefined;
  if (e?.code === "MANUAL_FORBIDDEN") return t("manualManagement.create.forbidden");
  if (e?.code === "CATEGORY_NOT_ATTACHED") return t("manualManagement.create.categoryGone");
  if (e?.status === 400) return t("manualManagement.create.tooLong");
  return t("manualManagement.create.failed");
}
